import { state } from './state.js';

const DATAEXPLORER_LOCK_KEY = 'wingman:dataexplorer:lock:v1';
const DATAEXPLORER_LOCK_ALERT_KEY = 'wingman:dataexplorer:lock-alert:v1';
const DATAEXPLORER_TAB_KEY = 'wingman:dataexplorer:tab:v1';
const DATAEXPLORER_CHANNEL_NAME = 'wingman:dataexplorer:channel:v1';
const DATAEXPLORER_HEARTBEAT_MS = 15000;
const DATAEXPLORER_STALE_MS = 90000;
const DATAEXPLORER_CLAIM_WAIT_MS = 250;
const DATAEXPLORER_ICON_ID = 'dataexplorer_lock_icon';
const DATAEXPLORER_MAX_SESSIONS_REDIRECT_FLAG = 'dataExplorerMaxSessionsRedirect';

function isDataExplorerPath() {
  return window.location.pathname.startsWith('/dataexplorer');
}

function buildMaxSessionsUrl() {
  const currentUrl = new URL(window.location.href);
  const customer = currentUrl.searchParams.get('customer');
  const maxSessionsUrl = new URL('/pages/max-sessions', window.location.origin);

  if (customer) {
    maxSessionsUrl.searchParams.set('customer', customer);
  }

  return maxSessionsUrl.pathname + maxSessionsUrl.search;
}

function resolveMaxSessionsRedirectFeatureFlag() {
  const featureFlags = window.WingmanFeatureFlags;

  if (!featureFlags || typeof featureFlags !== 'object') {
    return undefined;
  }

  const featureFlagValue = featureFlags[DATAEXPLORER_MAX_SESSIONS_REDIRECT_FLAG];

  if (typeof featureFlagValue === 'function') {
    return featureFlagValue() !== false;
  }

  if (typeof featureFlagValue === 'boolean') {
    return featureFlagValue;
  }

  return undefined;
}

function isMaxSessionsRedirectEnabled() {
  const featureFlagValue = resolveMaxSessionsRedirectFeatureFlag();

  if (typeof featureFlagValue === 'boolean') {
    return featureFlagValue;
  }

  const activeState = window.WingmanState && typeof window.WingmanState === 'object'
    ? window.WingmanState
    : state;

  return activeState.dataexplorer_max_sessions_redirect !== false;
}

function redirectToMaxSessions(options = {}) {
  if (!isMaxSessionsRedirectEnabled()) {
    return false;
  }

  if (typeof options.beforeRedirect === 'function') {
    options.beforeRedirect();
  }

  window.location.replace(buildMaxSessionsUrl());
  return true;
}

function getTabId() {
  let tabId = window.sessionStorage.getItem(DATAEXPLORER_TAB_KEY);

  if (!tabId) {
    tabId = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(DATAEXPLORER_TAB_KEY, tabId);
  }

  return tabId;
}

function readLock() {
  try {
    const lockRaw = window.localStorage.getItem(DATAEXPLORER_LOCK_KEY);

    if (!lockRaw) {
      return null;
    }

    return JSON.parse(lockRaw);
  } catch {
    return null;
  }
}

function readLockAlert() {
  try {
    const alertRaw = window.localStorage.getItem(DATAEXPLORER_LOCK_ALERT_KEY);

    if (!alertRaw) {
      return null;
    }

    return JSON.parse(alertRaw);
  } catch {
    return null;
  }
}

function writeLock(tabId) {
  const lockPayload = {
    tabId,
    timestamp: Date.now()
  };

  window.localStorage.setItem(DATAEXPLORER_LOCK_KEY, JSON.stringify(lockPayload));
  return lockPayload;
}

function isLiveForeignLock(lockPayload, tabId) {
  if (!lockPayload || lockPayload.tabId === tabId) {
    return false;
  }

  const lockTimestamp = Number(lockPayload.timestamp || 0);
  return Date.now() - lockTimestamp < DATAEXPLORER_STALE_MS;
}

function ownsLock(lockPayload, tabId) {
  return !!lockPayload && lockPayload.tabId === tabId;
}

function setLockAlert(tabId) {
  window.localStorage.setItem(DATAEXPLORER_LOCK_ALERT_KEY, JSON.stringify({
    tabId,
    timestamp: Date.now()
  }));
}

function clearLockAlert(tabId) {
  const alertPayload = readLockAlert();

  if (!alertPayload) {
    return;
  }

  if (!tabId || alertPayload.tabId === tabId) {
    window.localStorage.removeItem(DATAEXPLORER_LOCK_ALERT_KEY);
  }
}

function ensureLockIconElement() {
  const dataExplorerLink = document.getElementById('link_data_explorer');
  const dataExplorerText = dataExplorerLink && dataExplorerLink.querySelector('.sidebar-text');

  if (!dataExplorerLink || !dataExplorerText) {
    return null;
  }

  let lockIcon = document.getElementById(DATAEXPLORER_ICON_ID);
  if (lockIcon) {
    return lockIcon;
  }

  lockIcon = document.createElement('span');
  lockIcon.id = DATAEXPLORER_ICON_ID;
  lockIcon.textContent = '🔒';
  lockIcon.setAttribute('aria-label', 'Data Explorer lock status');
  lockIcon.style.display = 'none';
  lockIcon.style.marginLeft = '8px';
  lockIcon.style.fontSize = '14px';
  lockIcon.style.verticalAlign = 'middle';
  lockIcon.style.lineHeight = '1';

  dataExplorerText.insertAdjacentElement('beforeend', lockIcon);
  return lockIcon;
}

function isBlockedFromDataExplorer() {
  return isLiveForeignLock(readLock(), getTabId());
}

function renderLockIcon() {
  const lockIcon = ensureLockIconElement();

  if (!lockIcon) {
    return;
  }

  if (!isMaxSessionsRedirectEnabled()) {
    lockIcon.style.display = 'none';
    return;
  }

  const alertPayload = readLockAlert();
  const blockedByOtherTab = isBlockedFromDataExplorer();
  const showAlert = blockedByOtherTab && !!alertPayload;

  lockIcon.style.display = blockedByOtherTab ? 'inline-block' : 'none';
  lockIcon.style.color = showAlert ? '#dc3545' : '#000000';
}

function initDataExplorerLinkGuard() {
  const bindWhenSidebarReady = function () {
    const dataExplorerLink = document.getElementById('link_data_explorer');

    if (!dataExplorerLink) {
      return false;
    }

    if (!dataExplorerLink.dataset.lockGuardBound) {
      dataExplorerLink.dataset.lockGuardBound = 'true';
      dataExplorerLink.addEventListener('click', function (event) {
        if (isBlockedFromDataExplorer()) {
          if (redirectToMaxSessions()) {
            event.preventDefault();
          }
        }
      });
    }

    return true;
  };

  if (!bindWhenSidebarReady()) {
    const observer = new MutationObserver(function () {
      if (bindWhenSidebarReady()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

function initDataExplorerLockIcon() {
  const renderWhenSidebarReady = function () {
    if (ensureLockIconElement()) {
      renderLockIcon();
      return true;
    }

    return false;
  };

  if (!renderWhenSidebarReady()) {
    const observer = new MutationObserver(function () {
      if (renderWhenSidebarReady()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener('storage', function (event) {
    if (event.key === DATAEXPLORER_LOCK_KEY || event.key === DATAEXPLORER_LOCK_ALERT_KEY) {
      renderLockIcon();
    }
  });

  window.setInterval(function () {
    renderLockIcon();
  }, 5000);
}

function initDataExplorerLock() {
  if (!isDataExplorerPath()) {
    return;
  }

  const tabId = getTabId();
  let heartbeatId = null;
  let broadcastChannel = null;
  let claimTimeoutId = null;
  let hasRedirected = false;
  let isInitializedOwner = false;

  const redirectToMaxSessionsSafely = function () {
    if (hasRedirected) {
      return false;
    }

    return redirectToMaxSessions({
      beforeRedirect: function () {
        hasRedirected = true;
        if (claimTimeoutId) {
          clearTimeout(claimTimeoutId);
          claimTimeoutId = null;
        }
        if (heartbeatId) {
          window.clearInterval(heartbeatId);
          heartbeatId = null;
        }
        if (broadcastChannel) {
          broadcastChannel.close();
          broadcastChannel = null;
        }
      }
    });
  };

  const releaseLock = function () {
    const currentLock = readLock();

    if (ownsLock(currentLock, tabId)) {
      window.localStorage.removeItem(DATAEXPLORER_LOCK_KEY);
      clearLockAlert(tabId);
    }

    if (broadcastChannel) {
      broadcastChannel.postMessage({
        type: 'owner-released',
        tabId
      });
    }
  };

  const refreshLock = function () {
    const currentLock = readLock();

    if (isLiveForeignLock(currentLock, tabId)) {
      redirectToMaxSessionsSafely();
      return false;
    }

    writeLock(tabId);
    isInitializedOwner = true;

    if (!ownsLock(readLock(), tabId)) {
      redirectToMaxSessionsSafely();
      return false;
    }

    return true;
  };

  if (isBlockedFromDataExplorer()) {
    redirectToMaxSessionsSafely();
    return;
  }

  if (window.BroadcastChannel) {
    broadcastChannel = new window.BroadcastChannel(DATAEXPLORER_CHANNEL_NAME);
    broadcastChannel.addEventListener('message', function (event) {
      const message = event.data || {};

      if (message.tabId === tabId) {
        return;
      }

      if (message.type === 'claim-check') {
        const currentLock = readLock();

        if (ownsLock(currentLock, tabId) || isInitializedOwner) {
          setLockAlert(tabId);
          renderLockIcon();
          broadcastChannel.postMessage({
            type: 'owner-present',
            tabId
          });
        }
      }

      if (message.type === 'owner-present') {
        redirectToMaxSessionsSafely();
      }
    });

    broadcastChannel.postMessage({
      type: 'claim-check',
      tabId
    });
  }

  claimTimeoutId = window.setTimeout(function () {
    claimTimeoutId = null;

    if (!refreshLock()) {
      return;
    }

    heartbeatId = window.setInterval(function () {
      refreshLock();
    }, DATAEXPLORER_HEARTBEAT_MS);
  }, DATAEXPLORER_CLAIM_WAIT_MS);

  window.addEventListener('storage', function (event) {
    if (event.key !== DATAEXPLORER_LOCK_KEY || !isDataExplorerPath()) {
      return;
    }

    const currentLock = readLock();
    if (isLiveForeignLock(currentLock, tabId)) {
      redirectToMaxSessionsSafely();
    }
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      refreshLock();
    }
  });

  window.addEventListener('pagehide', function () {
    if (heartbeatId) {
      window.clearInterval(heartbeatId);
      heartbeatId = null;
    }
    releaseLock();

    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
    }
  });

  window.addEventListener('beforeunload', function () {
    if (heartbeatId) {
      window.clearInterval(heartbeatId);
      heartbeatId = null;
    }
    releaseLock();

    if (broadcastChannel) {
      broadcastChannel.close();
      broadcastChannel = null;
    }
  });
}

initDataExplorerLockIcon();
initDataExplorerLinkGuard();
initDataExplorerLock();